import {useEffect, useRef, useState} from 'react';

import {Paper, Typography} from '@mui/material';
import Box from '@mui/material/Box';
import Grid from '@mui/material/Grid';
import * as Sentry from '@sentry/react';
import {models, powerbi} from 'powerbi-client';
import {PowerBIEmbed} from 'powerbi-client-react';
import {arrayOf, bool, func, shape, string} from 'prop-types';
import {useLocation} from 'react-router-dom';

import {POWER_BI_REPORT_FILTERS, POWER_BI_SLICERS, DEFAULT_PROJECTS_SCHEMAS, REPORT_LOADING_STEPS, POWER_BI_PROJECT_SLICER_NAME, SPREADSHEET_TARGETS, POWER_BI_VISUALS} from '../../const';
import UpdateReportSummaryModal from './analysis/UpdateReportSummaryModal/UpdateReportSummaryModal';
import EditableReportButtons from './buttons/EditableReportButtons/EditableReportButtons';
import AccountantDocumentRequestModal from './document-request/AccountantDocumentRequestModal/AccountantDocumentRequestModal';
import ReportExportTypeChoiceModal from './export/ReportExportTypeChoiceModal/ReportExportTypeChoiceModal';
import CreateOrUpdateBudgetDialog from './forecast-and-budget/CreateOrUpdateBudgetDialog/CreateOrUpdateBudgetDialog';
import DeleteBudgetDialog from './forecast-and-budget/DeleteBudgetDialog/DeleteBudgetDialog';
import ExportBudgetModal from './forecast-and-budget/ExportBudgetModal/ExportBudgetModal';
import ImportBudgetFileModal from './forecast-and-budget/ImportBudgetFileModal/ImportBudgetFileModal';
import SpreadsheetModal from './forecast-and-budget/SpreadsheetModal';
import useEmbedReportLoadingTracking from './hooks/useEmbedReportLoadingTracking/useEmbedReportLoadingTracking';
import {useReportCleanup} from './hooks/useReportCleanup/useReportCleanup';
import SaveEditedReportBeforeLeavingModal from './report-saving/SaveEditedReportBeforeLeavingModal/SaveEditedReportBeforeLeavingModal';
import SaveReportViewModal from './report-views/SaveReportViewModal/SaveReportViewModal';
import ExportDataLoadingSpinner from './spinners/ExportDataLoadingSpinner/ExportDataLoadingSpinner';
import ReportLoadingSpinner from './spinners/ReportLoadingSpinner/ReportLoadingSpinner';
import useWindowSize from '../../hooks/dom/useWindowSize';
import useReportContainerAutoResize from '../../hooks/misc/useReportContainerAutoResize';
import useBanner from '../../hooks/providers/useBanner';
import useBudget from '../../hooks/providers/useBudget';
import useChatbot from '../../hooks/providers/useChatbot';
import useProfile from '../../hooks/providers/useProfile';
import useProjects from '../../hooks/providers/useProjects';
import useReport from '../../hooks/providers/useReport';
import useReportSummary from '../../hooks/providers/useReportSummary';
import useReportViews from '../../hooks/providers/useReportViews';
import useWorkspaces from '../../hooks/providers/useWorkspaces';
import {
  generateUniqueIdFromProjectName,
  saveCurrentProjectToLocalStorage,
  setProjectFilterSlicer,
  getReportIframeHeight,
  getVisualsUserCanSendThroughChatbot,
  isMobile,
  cleanSummaryVisualData
} from '../../utils';
import EmmaiMonthlyRestitutionModal from '../emmai-forms/forms-components/forms/EmmaiMonthlyRestitutionModal/EmmaiMonthlyRestitutionModal';
import Button from '../form/buttons/Button/Button';
import BaseModal from '../modal/BaseModal';
import CustomizeMappingModal from '../project/custom-mappings/CustomizeMappingModal';
import ResetCustomMappingsConfirmationModal from '../project/custom-mappings/ResetCustomMappingsConfirmationModal/ResetCustomMappingsConfirmationModal';
import DeleteReportModal from '../workspaces/DeleteWorkspaceOrReportModal';
import CreateOrImportReportModal from '../workspaces/import-or-create-report/CreateOrImportReportModal/CreateOrImportReportModal';
import SelectWorkspaceBeforeReportCreationModal from '../workspaces/import-or-create-report/SelectWorkspaceBeforeReportCreationModal/SelectWorkspaceBeforeReportCreationModal';

// eslint-disable-next-line complexity
const EmbedReport = ({updateToken, reportConfig, schemas = [], isCustomReport = false, lastPageBeforeReload}) => {
  const {
    setSelectedReport,
    setSelectedReportId,
    selectedReport,
    userCanUploadVisuals,
    setUserCanUploadVisuals,
    selectedProject,
    setSelectedProject,
    isReportInEditionMode,
    setIsReportInEditionMode,
    setIsEditableReportSaveAsModalOpen,
    reportSelectedPage,
    setReportSelectedPage,
    isDesktopLayout,
    reports,
    isReportLoaded,
    setIsReportLoaded,
    getAllReportVisuals,
    setAllReportVisuals,
    reportSelectedFoldersSirens,
    setReportSelectedFoldersSirens,
    isLoadingHidden,
    setIsLoadingHidden,
    isSlicerLoading,
    setIsSlicerLoading,
    loadReports,
    isReportEditable
  } = useReport();
  const {setIsSpreadsheetModalOpen, setSelectedBudget, setSpreadsheetDataLoaded} = useBudget();
  const {workspaces, isUserInWorkspace} = useWorkspaces();
  const {isSomeBannerVisible} = useBanner();
  const {allReportsViews, setIsReportPristine} = useReportViews();
  const {setCurrentReportPageSummary} = useReportSummary();
  const {profile} = useProfile();

  const currentReport = reports.find(r => r.report_id === selectedReport?.config?.id);

  const {projects, fetchProjects} = useProjects();
  const projectsRef = useRef(projects);
  const {isChatbotSidebarOpen, setSendableReportVisuals, isNavigatingToOtherReportPageToExportData} = useChatbot();

  const isProjectSlicerLoading = selectedProject && isSlicerLoading;

  const [shouldReloadReport, setShouldReloadReport] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const [reportHasEncounteredFatalError, setReportHasEncounteredFatalError] = useState(false);
  const [reportLoadingStep, setReportLoadingStep] = useState(REPORT_LOADING_STEPS.REPORT_LOADING);
  const [isFullScreenMode, setIsFullscreenMode] = useState(false);
  const [isReportReloading, setIsReportReloading] = useState(false);

  const isDeFiModule = schemas.includes(DEFAULT_PROJECTS_SCHEMAS.gestion);

  const reportViews = allReportsViews?.filter(v => v.report_id.toLowerCase() === selectedReport?.config?.id && v?.siren?.toString() === selectedProject?.siren?.toString());
  const lastReportAutomaticSave = reportViews.find(rv => !rv.is_visible);

  const location = useLocation();
  const windowSize = useWindowSize();

  const reportWorkspace = workspaces.find(w => (w.reports || []).find(r => r.report_id === selectedReport?.config?.id) !== undefined);
  const userIsInWorkspace = isUserInWorkspace(profile?.id, reportWorkspace);
  const shouldDisplayEditReportButtons = isReportEditable && isDesktopLayout && userIsInWorkspace;

  const size = {
    height: windowSize.height,
    width: windowSize.width
  };

  useEffect(() => {
    setSpreadsheetDataLoaded(false);
  }, [selectedProject?.siren]);

  useEffect(() => {
    if (!isLoading && isLoadingHidden && (!isProjectSlicerLoading || !currentReport?.homepage)) {
      setIsReportLoaded(true);
    } else {
      setIsReportLoaded(false);
    }
  }, [isLoading, isProjectSlicerLoading, isLoadingHidden]);

  useEmbedReportLoadingTracking({
    isLoading,
    isLoadingHidden,
    isProjectSlicerLoading,
    selectedReport,
    selectedProject,
    isSlicerLoading
  });

  const reportContainerId = 'report-container';
  useReportCleanup(selectedReport, reportContainerId);

  useReportContainerAutoResize();

  const applyLastReportView = async () => {
    await selectedReport.bookmarksManager.applyState(lastReportAutomaticSave.report_state);
  };

  const redirectToReportHomepage = async () => {
    const reportHomepage = currentReport?.homepage;
    const project = projects.find(p => p.siren === reportSelectedFoldersSirens[0] && currentReport.schema_name.includes(p.schema_name));
    if (!project) return;

    await setProjectFilterSlicer(selectedReport, project.project_name);

    if (reportHomepage) {
      await selectedReport.setPage(reportHomepage);
    }

    setIsLoadingHidden(true);
  };

  const setSummaryVisualData = async () => {
    const allPageVisuals = await reportSelectedPage.getVisuals();
    const summaryVisual = allPageVisuals.find(v => v.title === POWER_BI_VISUALS.summary.title);

    if (summaryVisual) {
      const {data} = await summaryVisual.exportData(models.ExportDataType.Summarized);
      const cleanedData = cleanSummaryVisualData(data);
      setCurrentReportPageSummary(cleanedData);
    } else {
      setCurrentReportPageSummary(null);
    }
  };

  useEffect(() => {
    const isLandscapeLayout = reportConfig.settings && reportConfig.settings.layoutType === models.LayoutType.Master;
    const isMobileDevice = isMobile();
    if (isLandscapeLayout && isMobileDevice && selectedReport && location.pathname.startsWith('/reports')) {
      setIsFullscreenMode(true);
    } else {
      setIsFullscreenMode(false);
    }
  }, [selectedReport, reportConfig.settings, location.pathname]);

  const endLoading = () => {
    setIsLoading(false);
    setIsLoadingHidden(true);
  };

  const onReportEvent = async event => {
    console.log({event});
    setIsReportPristine(false);
  };

  const onError = async event => {
    const error = event.detail;
    endLoading();

    // eslint-disable-next-line no-console
    console.error({
      name: error.name,
      message: error.message,
      detailedMessage: error.detailedMessage,
      level: error.level
    });

    // If the error level isn't Fatal, log the error and continue.
    if (error.level !== models.TraceType.Fatal) {
      return;
    }

    // If the Fatal error is TokenExpired, refresh the token.
    // Embed token has expired, we regenerate it via loadReports (make an API call to /get-embed-token)
    if (error.message === models.CommonErrorCodes.TokenExpired) {
      // eslint-disable-next-line no-console
      console.warn('EMBED TOKEN EXPIRED');
      setShouldReloadReport(true);
      setIsLoading(true);
      setIsLoadingHidden(false);
    } else {
      setReportHasEncounteredFatalError(true);

      const isDevelopment = import.meta.env.MODE === 'development';
      if (!isDevelopment) {
        Sentry.captureException(error);
      }
    }
  };

  useEffect(() => {
    projectsRef.current = projects;
  }, [projects]);

  // eslint-disable-next-line complexity
  function onDataSelected(event) {
    onReportEvent();

    // eslint-disable-next-line no-console
    console.log({event});

    const data = event.detail;
    let reportFilterChanged = POWER_BI_REPORT_FILTERS.filter(f => f.title === data.visual.title);
    // Sometimes, two slicers have the exact same title, so we have to compare title AND name of slicer
    reportFilterChanged = reportFilterChanged?.length > 1 ? POWER_BI_REPORT_FILTERS.find(f => f.name === data.visual.name) : reportFilterChanged[0];

    // When we are on filters page, if user is switching project from slicer, we redirect to home page
    if (reportFilterChanged?.title === POWER_BI_PROJECT_SLICER_NAME) {
      const noProjectSelected = data.dataPoints.length === 0 || data.dataPoints[0].identity[0].equals === null;
      if (noProjectSelected) {
        setReportSelectedFoldersSirens([]);
        setSelectedProject(null);
      } else if (data.dataPoints.length === 1) {
        const latestProjects = projectsRef.current;
        const projectName = data.dataPoints[0].identity[0].equals;
        const project = latestProjects?.find(p => p.project_name === projectName);

        setSelectedProject(project);
        setReportSelectedFoldersSirens([project?.siren]);
        saveCurrentProjectToLocalStorage(data);
      } else {
        const sirens = [];
        data.dataPoints.forEach(item => {
          const projectName = item.identity[0].equals;
          const siren = projects?.find(p => p.project_name === projectName)?.siren;
          sirens.push(siren);
        });
        setReportSelectedFoldersSirens(sirens);
      }
    }

    // When we are on project choice page, we listen to data change in order to set project in localStorage
    if (data.visual.name === POWER_BI_SLICERS.projectChoice.id) {
      saveCurrentProjectToLocalStorage(data);
    }
  }

  const onPageChanged = async event => {
    onReportEvent();
    const page = event.detail.newPage;
    setReportSelectedPage(page);

    // Business rule : user can start uploading files inside chatbot if :
    // - We are on a DeFi report, and he has landed on home page`
    const reportHomepage = reports.find(r => r.report_id === reportConfig.id)?.homepage;
    const isDeFiHomepage = isDeFiModule && page.name === reportHomepage;
    if (isDeFiHomepage) {
      setUserCanUploadVisuals(true);
    }
  };

  const onSavedAs = event => {
    onReportEvent();
    // eslint-disable-next-line no-console
    console.log({event});
    setIsEditableReportSaveAsModalOpen(true);
  };

  // This hook aims to fetch all report visuals once report is loaded
  useEffect(() => {
    (async () => {
      if (selectedReport && !isLoading) {
        const allVisuals = await getAllReportVisuals();
        setAllReportVisuals(allVisuals);
      }
    })();
  }, [selectedReport, isLoading]);

  // This hook fetches projects if necessary (not fetched before and it's a DeFi Previ OR DeFi Gestion report)
  useEffect(() => {
    (async () => {
      const shouldFetchProjects = (projects || []).length === 0;

      if (isDeFiModule && shouldFetchProjects) {
        await fetchProjects();
      }
    })();
  }, [schemas]);

  useEffect(() => {
    if (shouldReloadReport) return;

    const interval = setInterval(async () => {
      try {
        if (isReportLoaded && selectedReport && reportSelectedPage) {
          await setSummaryVisualData();
        }
      } catch (e) {
        console.log(e);
      }
    }, 500);

    return () => clearInterval(interval);
  }, [isReportLoaded, reportSelectedPage, selectedProject, shouldReloadReport]);

  useEffect(() => {
    (async () => {
      try {
        if (!isLoading && selectedReport && lastPageBeforeReload) {
          await selectedReport.setPage(lastPageBeforeReload.name);
          await setProjectFilterSlicer(selectedReport, selectedProject.project_name);
        }
      } catch (e) {
        // eslint-disable-next-line no-console
        console.error(e);
        Sentry.captureException(e);
      }
    })();
  }, [selectedReport, isLoading]);

  // This hook sets project in localStorage when user comes from projects page
  useEffect(() => {
    if (selectedProject) {
      localStorage.setItem('project', generateUniqueIdFromProjectName(selectedProject?.project_name));
    }
  }, [selectedProject]);

  // This hook sets the report in spinners state when we change report
  // Eg: from url 'reports/1234' to 'reports/5678'
  useEffect(() => {
    setIsLoading(true);
    setIsLoadingHidden(false);
    setSendableReportVisuals([]);
    setUserCanUploadVisuals(false);
    setSelectedBudget(null);
    setIsSlicerLoading(true);
    setReportLoadingStep(REPORT_LOADING_STEPS.REPORT_LOADING);
    setIsReportLoaded(false);
    // setReportSelectedFoldersSirens([]);
    setCurrentReportPageSummary(null);
    setIsReportPristine(true);
  }, [location.pathname]);

  // This hook aims to reset selected project when coming to a custom report
  useEffect(() => {
    if (isCustomReport) {
      setSelectedProject(null);
    }
  }, [selectedReport, isCustomReport]);

  // This hooks handles change on Project slicer in report
  // When user selects a new project, he's redirected on report's homepage
  useEffect(() => {
    (async () => {
      setIsSpreadsheetModalOpen(false);
      if (selectedReport && selectedProject && !isLoading) {
        if (lastReportAutomaticSave) {
          await applyLastReportView();
          return;
        }
        await redirectToReportHomepage();
      }
    })();
    // }, [reportViews]);
  }, [selectedProject]);

  // This hook aims to apply last report view auto-saved for custom reports
  useEffect(() => {
    if (isCustomReport && lastReportAutomaticSave && !isLoading) {
      applyLastReportView();
    }
  }, [isLoading, selectedReport]);

  // This hook handles when user comes from project page.
  // It automatically sets the chosen project in project choice page
  useEffect(() => {
    if (!selectedReport || isLoading || !selectedProject) return;

    const handleError = error => {
      console.error(error); // eslint-disable-line no-console
      Sentry.captureException(error);
    };

    const handleDefaultRedirect = async () => {
      try {
        setReportSelectedFoldersSirens([selectedProject.siren]);
        await redirectToReportHomepage();
      } catch (error) {
        handleError(error);
      }
    };

    const handleReportLoad = async () => {
      try {
        if (!currentReport?.homepage) {
          setIsLoadingHidden(true);
          return;
        }

        setReportLoadingStep(REPORT_LOADING_STEPS.PROJECT_BOOKMARK_APPLYING);
        setIsSlicerLoading(true);

        if (lastReportAutomaticSave) {
          await applyLastReportView();
          return;
        }

        if (lastPageBeforeReload || isCustomReport) return;

        await handleDefaultRedirect();
      } catch (error) {
        handleError(error);
      } finally {
        setIsSlicerLoading(false);
      }
    };

    handleReportLoad();
  }, [isLoading, selectedReport, selectedProject]);

  // Hook to get all report visuals and extract the ones user can send through copilot
  useEffect(() => {
    (async () => {
      if (selectedReport && userCanUploadVisuals) {
        try {
          const sendableVisuals = await getVisualsUserCanSendThroughChatbot(selectedReport, isCustomReport);
          setSendableReportVisuals(sendableVisuals);
        } catch (e) {
          // eslint-disable-next-line no-console
          console.error(e);
        }
      }
    })();
  }, [selectedReport, userCanUploadVisuals]);

  // Business rule : user can start uploading files inside chatbot if :
  // - We are on a custom report
  // - We are on reports page
  useEffect(() => {
    (async () => {
      if (selectedReport && isCustomReport && location.pathname.startsWith('/reports')) {
        setUserCanUploadVisuals(true);
      }
    })();
  }, [selectedReport]);

  // Hook to reset edit button state when switching report or switching route
  useEffect(() => {
    (async () => {
      if (selectedReport) {
        setIsReportInEditionMode(false);
      }
    })();
  }, [selectedReport, location.pathname]);

  useEffect(() => {
    (async () => {
      if (shouldReloadReport && selectedReport) {
        await updateToken();
        selectedReport.reload();
        setShouldReloadReport(false);
      }
    })();
  }, [shouldReloadReport, selectedReport]);

  // eslint-disable-next-line complexity
  const getCssClassname = () => {
    if (isFullScreenMode && isReportLoaded) {
      return 'report-fullscreen';
    }

    if (!isReportLoaded || isNavigatingToOtherReportPageToExportData) {
      return selectedReport?.config?.settings?.layoutType === models.LayoutType.MobilePortrait ? 'report-hidden-mobile' : 'report-hidden';
    }
    let cssClass = 'report-embed';

    if (shouldDisplayEditReportButtons && isReportInEditionMode) {
      cssClass += ` with-buttons`;
    }

    if (isChatbotSidebarOpen) {
      cssClass += ` is-chatbot-sidebar-open${isDesktopLayout ? '' : '-mobile'}`;
    }

    // Custom reports are a bit larger in height so we apply a custom class
    if (isCustomReport && isReportInEditionMode) {
      cssClass += ' custom-report';
    }

    return cssClass;
  };

  const bannerHeight = isSomeBannerVisible ? document.getElementsByClassName('MuiAlert-filled')[0]?.clientHeight : 0;
  const h = getReportIframeHeight(size.height, bannerHeight);
  const isReportPage = location.pathname.startsWith('/reports');
  const cssClass = getCssClassname();

  if (reportHasEncounteredFatalError) {
    // TODO Create component for this ?
    return (
      <Grid container alignItems="center" justifyContent="center" maxWidth="sm">
        <Paper elevation={2} sx={{p: 3}}>
          <Typography variant="h6" textAlign="center">
            L'application a rencontré une erreur inattendue, veuillez rafraichir la page
          </Typography>
          <Button
            disabled={isReportReloading}
            size="large"
            sx={{margin: '16px auto 0 auto', display: 'block'}}
            variant="contained"
            color="secondary"
            onClick={async () => {
              setIsReportReloading(true);
              setSelectedReport(null);
              await loadReports();
              setReportHasEncounteredFatalError(false);
              setIsReportReloading(false);
            }}
          >
            Rafraîchir
          </Button>
        </Paper>
      </Grid>
    );
  }

  const Container = isFullScreenMode ? BaseModal : Box;
  const modalProps = {
    withoutTitle: true,
    isFullScreen: true,
    slotProps: {
      backdrop: {
        sx: {
          backgroundColor: '#f5f5f5'
        }
      }
    },
    overridesPaperProps: {
      boxShadow: 'none !important',
      background: '#f5f5f5 !important',
      justifyContent: !isReportLoaded || isNavigatingToOtherReportPageToExportData ? 'center' : 'flex-start'
    }
  };

  return (
    <Container {...(isFullScreenMode ? modalProps : {})} height="100%" width="100%" open onClose={() => setIsFullscreenMode(false)}>
      <Grid
        id={reportContainerId}
        container
        flexDirection="column"
        alignItems="center"
        justifyContent={!isReportLoaded || isNavigatingToOtherReportPageToExportData ? 'center' : 'flex-start'}
        sx={{height: isReportPage ? '100%' : 0, maxHeight: h, opacity: isReportPage ? 1 : 0}}
        flexWrap="nowrap"
      >
        <PowerBIEmbed
          embedConfig={reportConfig}
          eventHandlers={
            new Map([
              ['dataSelected', onDataSelected],
              ['pageChanged', onPageChanged],
              ['buttonClicked', onReportEvent],
              ['visualClicked', onReportEvent],
              ['bookmarkApplied', onReportEvent],
              ['loaded', endLoading],
              ['error', onError],
              ['saveAsTriggered', onSavedAs]
            ])
          }
          getEmbeddedComponent={embedObject => {
            setSelectedReport(embedObject);
            setSelectedReportId(embedObject?.config.id);
          }}
          cssClassName={cssClass}
        />
        {!isReportLoaded && (
          <>
            <ReportLoadingSpinner loadingStep={reportLoadingStep} />
            <ExportDataLoadingSpinner />
          </>
        )}

        {isReportLoaded && (
          <>
            {currentReport ? (
              <>
                <EditableReportButtons shouldDisplay={shouldDisplayEditReportButtons} />
                {isReportInEditionMode ? <SaveEditedReportBeforeLeavingModal /> : null}
                <ReportExportTypeChoiceModal />
                <SaveReportViewModal />
              </>
            ) : null}

            {currentReport?.isCustomModel ? <DeleteReportModal isReportDeletion /> : null}
          </>
        )}
      </Grid>

      {selectedProject ? (
        <>
          <SpreadsheetModal target={SPREADSHEET_TARGETS.BUDGET} />
          <AccountantDocumentRequestModal />
          <DeleteBudgetDialog />
          <CreateOrUpdateBudgetDialog />
          <UpdateReportSummaryModal />
          <CustomizeMappingModal />
        </>
      ) : null}

      <CreateOrImportReportModal />
      <SelectWorkspaceBeforeReportCreationModal />
      <ImportBudgetFileModal />
      <ExportBudgetModal />
      <ResetCustomMappingsConfirmationModal />
      <EmmaiMonthlyRestitutionModal />
    </Container>
  );
};

EmbedReport.propTypes = {
  updateToken: func.isRequired,
  reportConfig: shape({}).isRequired,
  lastPageBeforeReload: shape({}),
  schemas: arrayOf(string),
  isCustomReport: bool
};

export default EmbedReport;
